/**
 * SOLOMON — DIALER PANEL (Dialer Phase 2 — Telnyx WebRTC softphone)
 *
 * Manual power-dial-assist operator screen. Small state machine:
 *   idle → calling-session(active) → { surfacing → dialing → connected | no_answer }
 *        → disposition → advance → (refill) → done
 *
 * Compliance invariants:
 *   - Operator taps Dial per call (no auto-dial).
 *   - Every surfaced number was gated server-side by isContactable AND each dial
 *     is authorized at /api/dialer/call/authorize immediately before origination
 *     (Issue 61): the server re-runs the gate and returns the destination number
 *     itself — this component never selects a number off the lead object.
 *   - Phase 2 dials in-browser via the Telnyx WebRTC SDK (.newCall). The token is
 *     minted server-side (/api/dialer/token); the browser never holds a raw secret.
 *
 * Disposition logging reuses the existing POST /api/calls/log.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { useAuth } from '../../context/AuthContext';
import DialMomentum from '../Wholesale/DialMomentum';
import './DialerPanel.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// The 8 valid dispositions (single source of truth: callQueueService).
const DISPOSITIONS = [
  { key: 'connected_interested',     label: 'Interested',      cls: 'disp-good' },
  { key: 'connected_not_interested', label: 'Not interested',  cls: 'disp-soft' },
  { key: 'connected_listed_with_agent', label: 'Listed w/ agent',  cls: 'disp-soft' },
  { key: 'no_answer',                label: 'No answer',        cls: 'disp-neutral' },
  { key: 'voicemail_left',           label: 'Left voicemail',   cls: 'disp-neutral' },
  { key: 'wrong_number',             label: 'Wrong number',     cls: 'disp-soft' },
  { key: 'disconnected',             label: 'Disconnected',     cls: 'disp-soft' },
  { key: 'dnc_requested',            label: 'Do not call',      cls: 'disp-stop' },
];

function formatCurrency(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return `$${Math.round(num).toLocaleString()}`;
}

// Repair common UTF-8 mojibake (from CSV double-encoding) so referral notes read cleanly.
function cleanNotes(s) {
  if (!s) return '';
  return String(s)
    .replace(/(ð[-￿]{1,2})+/g, '') // mangled bold-header runs (ð—£ð—¥…)
    .replace(/â€¢/g, '•')
    .replace(/â€"/g, '—')
    .replace(/â€“/g, '–')
    .replace(/â€™/g, "'")
    .replace(/â€˜/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/Â®/g, '®')
    .replace(/\u00C2/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

// Lookahead of every known label, so a field captures only up to the NEXT label.
const NOTE_STOP =
  '(?:\\basking price\\b|\\bmarket value\\b|\\blisting status\\b|\\blisting\\b|\\blisted\\b|' +
  '\\bbed\\s*\\/?\\s*bath\\b|\\bsq\\s*ft\\b|\\bsqft\\b|\\broof\\b|\\bac\\b|\\bkitchens?\\b|\\brepairs?\\b|' +
  '\\boverall condition\\b|\\bcondition\\b|\\boccupancy\\b|\\breason for selling\\b|\\bmotive\\b|\\bmotivation\\b|' +
  '\\bmortgage\\b|\\bcallback\\b|\\bclosing\\b|\\btiming\\b|\\bagent name\\b|\\balias name\\b|' +
  '\\bname\\b|\\baddress\\b|\\bphone\\b|\\be-?mail\\b|\\bap\\b|\\bmv\\b|\\bprice\\b|$)';

function noteField(text, labelPat) {
  const m = text.match(new RegExp(labelPat + '\\s*[:\\-]?\\s*(.*?)\\s*(?=' + NOTE_STOP + ')', 'i'));
  if (!m) return null;
  const v = (m[1] || '').replace(/^[\s:•\-–]+/, '').replace(/[\s•\-–,;]+$/, '').trim();
  if (!v || /^(n\/?a|na|none|none mentioned|no)$/i.test(v)) return null;
  return v.length > 140 ? `${v.slice(0, 140)}…` : v;
}

// Parse the freeform referral note into a clean list of qualifying fields only.
function parseNotes(raw) {
  const text = cleanNotes(raw)
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/e-?mail\s*:?\s*[^\s,;]*/gi, ' ')
    .replace(/[ \t]{2,}/g, ' ');
  if (!text) return [];
  const FIELDS = [
    ['Asking price', '(?:\\basking price\\b|\\bAP\\b)'],
    ['Market value', '(?:\\bmarket value\\b|\\bMV\\b)'],
    ['Beds / baths', '(?:\\bbed\\s*\\/?\\s*bath\\b)'],
    ['Sqft', '(?:\\bsq\\s*ft\\b|\\bsqft\\b)'],
    ['Condition', '(?:\\boverall condition\\b|\\bcondition\\b)'],
    ['Repairs', '(?:\\brepairs?\\b)'],
    ['Occupancy', '(?:\\boccupancy\\b)'],
    ['Motivation', '(?:\\breason for selling\\b|\\bmotive(?:\\s*for\\s*selling)?\\b|\\bmotivation\\b)'],
    ['Callback', '(?:\\bcallback\\s*time\\b|\\bcallback\\b)'],
    ['Closing', '(?:\\bclosing\\b)'],
  ];
  const out = [];
  for (const [label, pat] of FIELDS) {
    const v = noteField(text, pat);
    if (v) out.push({ label, value: v });
  }
  return out;
}

export default function DialerPanel({ onExit }) {
  const { authFetch } = useAuth();

  const [sessionId, setSessionId] = useState(null);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [callState, setCallState] = useState('surfacing'); // surfacing|dialing|connected|no_answer|done
  const [callNotes, setCallNotes] = useState('');          // operator notes for the CURRENT lead's attempt
  const [anchor, setAnchor] = useState(null);              // owned-comp anchor for current lead
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientReady, setClientReady] = useState(false);   // Telnyx WebRTC client connected + ready
  const [dialLogged, setDialLogged] = useState(0);         // momentum: bumps DialMomentum HUD on each logged dial

  // Free-text lead search — operator pulls up a SPECIFIC lead to dial instead
  // of the prioritized queue. A picked lead temporarily overrides queue[idx];
  // disposing it returns to the queue at the same position.
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [pickedLead, setPickedLead]       = useState(null);

  const clientRef = useRef(null);        // TelnyxRTC client (one per session)
  const callRef = useRef(null);          // the active Call, if any
  const callerNumberRef = useRef(null);  // owned Telnyx caller-ID (from /token)

  // A picked (searched) lead overrides the queue's current lead until disposed.
  const lead = pickedLead || queue[idx] || null;

  // Clear the notes scratchpad whenever we move to a different lead, so one
  // lead's notes never bleed into the next attempt.
  useEffect(() => { setCallNotes(''); }, [lead?.id]);

  // ── Telnyx WebRTC client lifecycle — one client per calling session ─────────
  // Mints a server-side token, connects, and tears down on session end/unmount.
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let client = null;

    (async () => {
      try {
        const r = await authFetch(`${API}/api/dialer/token`);
        const d = await r.json();
        if (!d.success || !d.data?.token) throw new Error(d.error || 'no dial token');
        if (cancelled) return;
        callerNumberRef.current = d.data.callerNumber || null;
        client = new TelnyxRTC({ login_token: d.data.token, remoteElement: 'telnyx-remote-audio' });
        client.on('telnyx.ready', () => { if (!cancelled) setClientReady(true); });
        client.on('telnyx.error', (e) => {
          if (!cancelled) setError(`Dialer connection error: ${e?.message || e?.code || 'unknown'}`);
        });
        // Call-state updates arrive on the CLIENT (the Call object has no .on() in
        // @telnyx/webrtc v2.27.1). One active call at a time → drive callState here.
        client.on('telnyx.notification', (n) => {
          if (cancelled || n?.type !== 'callUpdate' || !n.call) return;
          const st = n.call.state;
          if (st === 'active') {
            setCallState('connected');
          } else if (['new', 'requesting', 'trying', 'recovering', 'ringing', 'answering', 'early'].includes(st)) {
            setCallState('dialing');
          } else if (st === 'hangup' || st === 'destroy' || st === 'purge') {
            callRef.current = null;
            setCallState((prev) => (prev === 'connected' ? 'ended' : prev === 'dialing' ? 'no_answer' : prev));
          }
        });
        clientRef.current = client;
        client.connect();
      } catch (err) {
        if (!cancelled) setError(`Dialer init failed: ${err.message}`);
      }
    })();

    return () => {
      cancelled = true;
      setClientReady(false);
      try { callRef.current?.hangup?.(); } catch { /* best-effort */ }
      callRef.current = null;
      try { (client || clientRef.current)?.disconnect?.(); } catch { /* best-effort */ }
      clientRef.current = null;
    };
  }, [sessionId, authFetch]);

  const loadAnchor = useCallback(async (leadId) => {
    setAnchor(null);
    try {
      const r = await authFetch(`${API}/api/leads/${leadId}/owned-comps`);
      const d = await r.json();
      setAnchor(d.success ? d.data : null);
    } catch {
      setAnchor(null);
    }
  }, [authFetch]);

  // Debounced lead search. Skips while a call is live (don't yank the lead out
  // from under an active call) — the search UI is hidden then anyway.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${API}/api/dialer/search?q=${encodeURIComponent(q)}`);
        const d = await r.json();
        if (!cancelled) setSearchResults(d.success ? (d.data || []) : []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery, authFetch]);

  // Pick a specific searched lead to dial. Overrides the queue lead until it's
  // disposed; resets call UI so the operator can dial it cleanly.
  const pickLead = (chosen) => {
    setPickedLead(chosen);
    setSearchQuery('');
    setSearchResults([]);
    setCallState('surfacing');
    loadAnchor(chosen.id);
  };

  // Abandon the picked lead without dialing — fall back to the queue position.
  const clearPick = () => {
    setPickedLead(null);
    setCallState('surfacing');
    if (queue[idx]) loadAnchor(queue[idx].id);
  };

  const start = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await (await authFetch(`${API}/api/dialer/session/start`, { method: 'POST' })).json();
      if (!s.success) throw new Error(s.error || 'Could not start session');
      const sid = s.data.sessionId;
      setSessionId(sid);
      const q = await (await authFetch(`${API}/api/dialer/queue?sessionId=${sid}`)).json();
      const data = q.data || [];
      setQueue(data);
      setIdx(0);
      setCallState(data.length ? 'surfacing' : 'done');
      if (data[0]) loadAnchor(data[0].id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Phase 2: in-browser WebRTC dial. Operator taps Dial (human-initiated). The
  // server re-gates contactability (TCPA) before we originate; only on allow do
  // we place the call through the Telnyx SDK.
  const dial = async () => {
    if (!lead?.id) return;
    if (!clientReady || !clientRef.current) { setError('Dialer still connecting — give it a moment.'); return; }
    setError(null);

    // Server-authorized dial (Issue 61): the backend runs the TCPA gate and
    // returns the destination number ONLY on pass — the client never selects
    // a number off the lead object. NEVER originate when allowed === false.
    let destinationNumber = null;
    try {
      const auth = await (await authFetch(`${API}/api/dialer/call/authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id }),
      })).json();
      if (!auth.success) throw new Error(auth.error || 'authorization failed');
      if (!auth.data?.allowed || !auth.data?.destinationNumber) {
        setError(`Blocked by compliance gate: ${(auth.data?.reasons || []).join(', ') || 'not contactable'}`);
        setCallState('no_answer');
        return;
      }
      destinationNumber = auth.data.destinationNumber;
    } catch (err) {
      setError(`Could not authorize the call: ${err.message}`);
      return;
    }

    setCallState('dialing');
    try {
      // newCall returns a Call; state changes are observed on the client's
      // 'telnyx.notification' handler (wired in the session init effect) — the
      // Call object has no .on() in v2.27.1.
      callRef.current = clientRef.current.newCall({
        destinationNumber,
        callerNumber: callerNumberRef.current || undefined,
        remoteElement: 'telnyx-remote-audio',
        audio: true,
      });
    } catch (err) {
      setError(`Dial failed: ${err.message}`);
      setCallState('surfacing');
    }
  };

  const hangup = () => {
    try { callRef.current?.hangup?.(); } catch { /* best-effort */ }
    callRef.current = null;
  };

  const logAndAdvance = async (disposition, notes = null) => {
    setLoading(true);
    setError(null);
    const wasPicked = pickedLead;
    // End any live call before logging + advancing.
    try { callRef.current?.hangup?.(); } catch { /* best-effort */ }
    callRef.current = null;
    try {
      await authFetch(`${API}/api/calls/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id, disposition, notes }),
      });
      setDialLogged((n) => n + 1); // momentum: count this logged dial → HUD optimistic +1, pulse, refetch
      // A picked (searched) lead isn't part of the queue order — log it, then
      // drop back to the queue at the same position rather than advancing idx.
      if (wasPicked) {
        setPickedLead(null);
        if (queue[idx]) { setCallState('surfacing'); loadAnchor(queue[idx].id); }
        else setCallState('done');
        return;
      }
      const next = idx + 1;
      if (next >= queue.length) {
        // Refill from the continuous pull (session excludes already-attempted).
        const q = await (await authFetch(`${API}/api/dialer/queue?sessionId=${sessionId}`)).json();
        const data = q.data || [];
        setQueue(data);
        setIdx(0);
        if (data[0]) { setCallState('surfacing'); loadAnchor(data[0].id); }
        else setCallState('done');
      } else {
        setIdx(next);
        setCallState('surfacing');
        loadAnchor(queue[next].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    try { callRef.current?.hangup?.(); } catch { /* best-effort */ }
    callRef.current = null;
    if (sessionId) {
      try { await authFetch(`${API}/api/dialer/session/${sessionId}/stop`, { method: 'POST' }); } catch { /* best-effort */ }
    }
    setSessionId(null); // triggers the WebRTC client teardown effect
    setQueue([]);
    setIdx(0);
    setAnchor(null);
    setPickedLead(null);
    setSearchQuery('');
    setSearchResults([]);
    setCallState('surfacing');
  };

  // ── Owned-comp anchor derivation (mirror of LeadCalculator) ─────────────
  const ownedSupported = anchor?.supported === true;
  const ownedPool = ownedSupported ? (anchor.comps || []) : [];
  const ownedAgg = ownedSupported ? anchor.aggregate : null;
  const hasOwnedAnchor = ownedPool.length >= 3;
  const ownedTier = (ownedAgg?.total_count ?? 0) >= 50 ? 'high'
                  : (ownedAgg?.total_count ?? 0) >= 20 ? 'medium'
                  : 'low';

  // Lead search box — pull up and dial a SPECIFIC lead. Hidden during a live
  // call so we never swap the lead out from under an active connection.
  const inCall = callState === 'dialing' || callState === 'connected';
  const renderSearchBox = () => (
    <div className="dialer-search">
      <input
        type="text"
        className="dialer-search-input"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search any lead by name, address, or phone to call directly…"
        disabled={inCall}
      />
      {searchQuery.trim().length >= 2 && (
        <div className="dialer-search-results">
          {searching ? (
            <div className="dialer-search-empty">Searching…</div>
          ) : searchResults.length === 0 ? (
            <div className="dialer-search-empty">No contactable leads match.</div>
          ) : (
            searchResults.map((r) => (
              <button
                key={r.id}
                type="button"
                className="dialer-search-result"
                onClick={() => pickLead(r)}
                disabled={inCall}
              >
                <span className="dialer-search-result-addr">{r.property_address || '—'}</span>
                <span className="dialer-search-result-meta">
                  {[r.owner_full_name, r.phone_primary].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  // ── IDLE: not in a session ──────────────────────────────────────────────
  if (!sessionId) {
    return (
      <div className="dialer-panel">
        <div className="dialer-idle">
          <h2 className="dialer-title">Power Dial</h2>
          <p className="dialer-subtitle">
            Prioritized, TCPA-gated calling queue. You tap Dial per lead — no auto-dial.
            Callbacks-due first, then highest motivation. Numbers re-checked through the
            contactability gate before they surface and again before each call.
          </p>
          {error && <div className="dialer-error">{error}</div>}
          <button className="dialer-btn dialer-btn--primary" onClick={start} disabled={loading}>
            {loading ? 'Starting…' : 'Start calling'}
          </button>
        </div>
      </div>
    );
  }

  // ── DONE: queue exhausted for this session ──────────────────────────────
  if (callState === 'done') {
    return (
      <div className="dialer-panel">
        <div className="dialer-idle">
          <h2 className="dialer-title">Queue clear</h2>
          <p className="dialer-subtitle">
            No more contactable leads in this session. Re-eligible no-answers return
            next session after the cooldown. You can still search for a specific
            lead to call below.
          </p>
          {renderSearchBox()}
          <button className="dialer-btn dialer-btn--primary" onClick={stop}>End session</button>
        </div>
      </div>
    );
  }

  // ── ACTIVE: lead loop ───────────────────────────────────────────────────
  return (
    <div className="dialer-panel">
      {/* Remote call audio sink — the Telnyx SDK plays the other party's audio here. */}
      <audio id="telnyx-remote-audio" autoPlay />
      <div className="dialer-session-bar">
        <span className="dialer-session-tag">Session active</span>
        <span className="dialer-session-pos">Lead {idx + 1} of {queue.length} surfaced</span>
        <span className={`dialer-session-pos ${clientReady ? 'dialer-ready' : ''}`}>
          {clientReady ? '● dialer ready' : '○ connecting…'}
        </span>
        <button className="dialer-btn dialer-btn--ghost" onClick={stop}>Stop</button>
      </div>

      {error && <div className="dialer-error">{error}</div>}

      {renderSearchBox()}

      {pickedLead && (
        <div className="dialer-pick-banner">
          <span>Calling a searched lead — disposition returns you to the queue.</span>
          <button type="button" className="dialer-btn dialer-btn--ghost" onClick={clearPick} disabled={inCall}>
            Back to queue
          </button>
        </div>
      )}

      <DialMomentum variant="hud" refreshSignal={dialLogged} />

      {!lead ? (
        <div className="dialer-empty">Loading next lead…</div>
      ) : (
        <div className="dialer-card">
          {/* Lead header */}
          <div className="dialer-lead-head">
            <div className="dialer-lead-address">{lead.property_address}</div>
            {(lead.owner_full_name || lead.property_city || lead.property_state) && (
              <div className="dialer-lead-meta">
                {[lead.owner_full_name, [lead.property_city, lead.property_state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
              </div>
            )}
            <div className="dialer-lead-chips">
              {lead.property_zip && <span className="dialer-chip">{lead.property_zip}</span>}
              {lead.motivation_score != null && (
                <span className="dialer-chip dialer-chip--motivation">
                  motivation {lead.motivation_score}
                </span>
              )}
              {lead.callback_due_at && <span className="dialer-chip dialer-chip--callback">callback due</span>}
              {lead.last_disposition && (
                <span className="dialer-chip">{lead.last_disposition.replace(/_/g, ' ')}</span>
              )}
            </div>
          </div>

          {/* Qualifying notes — parsed to clean fields only (e.g. prequalified referral) */}
          {parseNotes(lead.notes).length > 0 && (
            <div className="dialer-qualifying-notes">
              <div className="dialer-qualifying-notes-heading">
                Qualifying notes
              </div>
              {parseNotes(lead.notes).map((f, i) => (
                <div key={i} className="dialer-qualifying-notes-row">
                  <span className="dialer-qualifying-notes-label">{f.label}</span>
                  <span className="dialer-qualifying-notes-value">{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Owned-comp anchor (free public-records surface) */}
          {hasOwnedAnchor ? (
            <div className={`dialer-comp-aggregate conf-${ownedTier}`}>
              <div className="dialer-comp-aggregate-header">
                <span className="dialer-comp-aggregate-label">Public records</span>
                <span className={`dialer-comp-aggregate-chip conf-${ownedTier}`}>
                  {ownedTier === 'high' ? 'STRONG' : ownedTier === 'medium' ? 'MODERATE' : 'THIN'}
                </span>
                <span className="dialer-comp-aggregate-meta">
                  {ownedAgg?.total_count ?? ownedPool.length} recorded sales · same zip · arm's-length · $0
                </span>
              </div>
              <div className="dialer-comp-aggregate-row">
                {ownedAgg?.avg_price_per_sqft != null && (
                  <span className="dialer-comp-stat">avg <strong>{formatCurrency(ownedAgg.avg_price_per_sqft)}/sqft</strong></span>
                )}
                {ownedAgg?.median_price != null && (
                  <span className="dialer-comp-stat">median sale <strong>{formatCurrency(ownedAgg.median_price)}</strong></span>
                )}
              </div>
            </div>
          ) : (
            <div className="dialer-comp-fallback" role="status">
              <span className="dialer-comp-fallback-icon" aria-hidden="true">⚠</span>
              No usable comp anchors — no neighbor-comp defense for this lead. Lead on price carefully.
            </div>
          )}

          {/* Operator script */}
          <div className="dialer-script">
            <div className="dialer-script-label">Script</div>
            <p>
              "Hi, I'm calling about your property at {lead.property_address}. I'm a local
              investor — are you open to a cash offer if the number made sense?"
            </p>
          </div>

          {/* Dial + phone */}
          <div className="dialer-dial-row">
            <div className="dialer-phone">{lead.phone_primary || 'no number on file'}</div>
            <button
              className="dialer-btn dialer-btn--primary"
              onClick={dial}
              disabled={!lead.phone_primary || loading || !clientReady || callState === 'dialing' || callState === 'connected'}
            >
              {!clientReady ? 'Connecting…'
                : callState === 'connected' ? 'On call'
                : callState === 'dialing' ? 'Calling…'
                : callState === 'ended' ? 'Redial'
                : 'Dial'}
            </button>
            {(callState === 'dialing' || callState === 'connected') && (
              <button className="dialer-btn dialer-btn--stop" onClick={hangup}>Hang up</button>
            )}
            <button className="dialer-btn dialer-btn--ghost" onClick={() => { hangup(); setCallState('no_answer'); }}>
              No answer
            </button>
          </div>

          {/* Live-VM prompt — never auto-audio */}
          {callState === 'connected' && (
            <div className="dialer-vm-prompt" role="status">
              Live call. <strong>If you reached their voicemail</strong>, wait for the beep
              and leave your message <strong>in your own voice</strong>, then hang up and tap
              "Left voicemail." Never play pre-recorded audio.
            </div>
          )}
          {callState === 'no_answer' && (
            <div className="dialer-vm-prompt" role="status">
              No pickup. If it rang out with no voicemail, pick a disposition. (To leave a
              live voicemail you must be connected to their mailbox first.)
            </div>
          )}
          {callState === 'ended' && (
            <div className="dialer-vm-prompt" role="status">
              Call ended. Pick a disposition below — or Redial.
            </div>
          )}

          {/* Live notes — operator jots while talking; saved with the disposition */}
          <div className="dialer-notes">
            <label className="dialer-notes-label" htmlFor="dialer-call-notes">Call notes</label>
            <textarea
              id="dialer-call-notes"
              className="dialer-notes-input"
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              placeholder="Jot notes while they talk — saved with the disposition."
              rows={3}
            />
          </div>

          {/* Disposition buttons */}
          <div className="dialer-dispositions">
            {DISPOSITIONS.map((d) => (
              <button
                key={d.key}
                className={`dialer-disp-btn ${d.cls}`}
                onClick={() => logAndAdvance(d.key, callNotes.trim() || null)}
                disabled={loading}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
